/**
 * `/admin/certificacoes` — o registro de certificação da plataforma.
 *
 * Spec: `docs/superpowers/specs/2026-09-28-selo-de-certificacao-design.md` §5.
 *
 * **Tela de administração, não dashboard.** Sem percentual de conformidade, sem anel, sem
 * contagem de controles: nenhum desses números muda decisão, e nenhum sai de fato nenhum
 * desta spec. O que muda decisão é o que vence primeiro, e é a ORDEM da lista que diz isso.
 *
 * **Renovar é registrar de novo**, nunca editar: o documento é outro, com outra data e outro
 * número. Editar o antigo apagaria o que a plataforma afirmou no ano anterior, que é
 * exatamente o que uma auditoria vem conferir. Por isso não existe botão de editar aqui.
 */
import { useMemo, useState } from 'react'
import {
  Alert, Anchor, Badge, Button, Group, Modal, Select, Stack, Table, Text, Textarea,
  TextInput, Title, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle, IconCirclePlus, IconFileCertificate, IconPlus, IconTrash,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import {
  FRAMEWORK_LABELS,
  type CertificadoResumo, type Framework, type TipoVinculo,
} from '@/api/certificacoes'
import { listKbDocuments } from '@/api/kb'
import { WidgetCard } from '@/components/ui'
import { ROTULOS, dataBr } from '@/components/certificacao/SeloDoAmbiente'
import {
  useCertificado, useCertificados, useDesvincularAmbiente, useVincularDocumento,
  useRegistrarCertificado, useRevogarCertificado, useVincularAmbiente,
} from '@/hooks/useCertificacoes'

const TIPO_LABELS: Record<TipoVinculo, string> = {
  escopo: 'No escopo do certificado',
  interno: ROTULOS.interno,
  transicao: 'Em transição para o ambiente certificado',
}

const TIPO_CORES: Record<TipoVinculo, string> = {
  escopo: 'inovareTeal',
  interno: 'blue',
  transicao: 'orange',
}

/** O chip da coluna Ambientes. O `Badge` do Mantine sobe a caixa, então a chave crua sairia
 *  como `TRANSICAO` — sem cedilha, que não é português. O rótulo inteiro fica no tooltip.
 */
export const TIPO_CURTO: Record<TipoVinculo, string> = {
  escopo: 'no escopo',
  interno: 'auditados',
  transicao: 'em transição',
}

const ESTADO_CORES: Record<string, string> = {
  vigente: 'teal', vencido: 'gray', revogado: 'gray',
}

function mensagemDeErro(err: unknown, padrao: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' ? detail : padrao
}

function diasAte(iso: string): number {
  const hoje = new Date()
  const alvo = new Date(`${iso.slice(0, 10)}T00:00:00`)
  return Math.round((alvo.getTime() - hoje.getTime()) / 86_400_000)
}

interface TenantOpcao { value: string; label: string; environment: string | null }

function useTenantsSelecionaveis(): TenantOpcao[] {
  // `/v1/tenants` devolve os tenants que QUEM ESTÁ OLHANDO alcança — inclusive para o perfil
  // AUDITOR, que é `all_tenants`. Vincular ambiente que a pessoa não vê não faria sentido, e
  // `/v1/admin/tenants` pede `admin:platform`, que o AUDITOR não tem.
  const { data } = useQuery({
    queryKey: ['tenants-para-vinculo'],
    queryFn: async () => (await api.get('/v1/tenants')).data.items ?? [],
  })
  return useMemo(() => (data ?? []).map((t: Record<string, unknown>) => ({
    value: String(t.tenant_id),
    label: `${t.tenant_name} (${String(t.environment ?? '?').toUpperCase()})`,
    environment: (t.environment as string) ?? null,
  })), [data])
}

export function CertificacoesAdmin() {
  const { data: certificados, isLoading, error } = useCertificados()
  const [detalheId, setDetalheId] = useState<number | null>(null)
  const [novoAberto, setNovoAberto] = useState(false)

  const lista: CertificadoResumo[] = certificados ?? []
  // Só entre os vigentes: dizer "vence em 12 dias" de um certificado revogado é ruído.
  const vencendo = lista.filter(
    (c) => c.estado === 'vigente' && diasAte(c.vence_em) <= 90)

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={3} c="var(--ai-text-primary)" m={0}>Certificações</Title>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setNovoAberto(true)}
                data-testid="certificacoes-novo">
          Registrar certificado
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Não foi possível carregar os certificados.
        </Alert>
      )}

      {vencendo.length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}
               title="Certificado perto do vencimento" data-testid="certificacoes-vencendo">
          <Text size="sm">
            {vencendo.map((c) => (
              <span key={c.id}>
                {FRAMEWORK_LABELS[c.framework]} vence em {diasAte(c.vence_em)} dia(s)
                {' '}({dataBr(c.vence_em)}).{' '}
              </span>
            ))}
            Renovar é <b>registrar o documento novo</b>, não editar este.
          </Text>
        </Alert>
      )}

      <WidgetCard>
        <Table highlightOnHover data-testid="certificacoes-tabela">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Framework</Table.Th>
              <Table.Th>Emissor</Table.Th>
              <Table.Th>Documento</Table.Th>
              <Table.Th>Vigência</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th>Ambientes</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr><Table.Td colSpan={7}>
                <Text c="dimmed" ta="center" py="md">Carregando…</Text>
              </Table.Td></Table.Tr>
            )}
            {!isLoading && lista.length === 0 && (
              <Table.Tr><Table.Td colSpan={7}>
                <Text c="dimmed" ta="center" py="md">
                  Nenhum certificado registrado. O registro é o que dá emissor, escopo escrito
                  e validade ao que hoje é só um campo marcado.
                </Text>
              </Table.Td></Table.Tr>
            )}
            {lista.map((c) => (
              <Table.Tr key={c.id} data-testid={`certificado-linha-${c.id}`}>
                <Table.Td>
                  <Text fw={600} size="sm">
                    {FRAMEWORK_LABELS[c.framework] ?? c.framework}
                    {c.versao_framework ? ` ${c.versao_framework}` : ''}
                  </Text>
                </Table.Td>
                <Table.Td><Text size="sm">{c.emissor}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" ff="monospace">
                    {c.documento_numero ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{dataBr(c.emitido_em)} → {dataBr(c.vence_em)}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={ESTADO_CORES[c.estado] ?? 'gray'}>
                    {c.estado}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {(['escopo', 'interno', 'transicao'] as TipoVinculo[]).map((t) => (
                      c.ambientes[t] > 0 ? (
                        <Tooltip key={t} label={TIPO_LABELS[t]}>
                          <Badge size="sm" variant="light" color={TIPO_CORES[t]}
                                 data-testid={`certificado-${c.id}-ambientes-${t}`}>
                            {c.ambientes[t]} {TIPO_CURTO[t]}
                          </Badge>
                        </Tooltip>
                      ) : null
                    ))}
                    {c.ambientes.escopo + c.ambientes.interno + c.ambientes.transicao === 0 && (
                      <Text size="sm" c="dimmed">nenhum</Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Button size="compact-sm" variant="subtle"
                          leftSection={<IconFileCertificate size={14} />}
                          onClick={() => setDetalheId(c.id)}
                          data-testid={`certificado-detalhe-${c.id}`}>
                    Detalhe
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </WidgetCard>

      <ModalRegistrar aberto={novoAberto} onFechar={() => setNovoAberto(false)} />
      {detalheId != null && (
        <ModalDetalhe id={detalheId} onFechar={() => setDetalheId(null)} />
      )}
    </Stack>
  )
}

function ModalRegistrar({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const registrar = useRegistrarCertificado()
  const [framework, setFramework] = useState<Framework>('pci_dss')
  const [versao, setVersao] = useState('')
  const [emissor, setEmissor] = useState('')
  const [documento, setDocumento] = useState('')
  const [escopo, setEscopo] = useState('')
  const [emitido, setEmitido] = useState('')
  const [vence, setVence] = useState('')
  const [documentoKb, setDocumentoKb] = useState<string | null>(null)

  const enviar = () => registrar.mutate({
    framework,
    versao_framework: versao.trim() || null,
    emissor: emissor.trim(),
    documento_numero: documento.trim() || null,
    escopo_texto: escopo.trim(),
    emitido_em: emitido.trim(),
    vence_em: vence.trim(),
    documento_kb_id: documentoKb ? Number(documentoKb) : null,
  }, {
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Certificado registrado.' })
      onFechar()
      setVersao(''); setEmissor(''); setDocumento(''); setEscopo('')
      setEmitido(''); setVence(''); setDocumentoKb(null)
    },
    onError: (e) => notifications.show({
      color: 'red', message: mensagemDeErro(e, 'Não foi possível registrar.'),
    }),
  })

  return (
    <Modal opened={aberto} onClose={onFechar} title="Registrar certificado" size="lg">
      <Stack gap="sm">
        <Select label="Framework" value={framework} allowDeselect={false}
                onChange={(v) => setFramework((v as Framework) ?? 'pci_dss')}
                data={[
                  { value: 'pci_dss', label: FRAMEWORK_LABELS.pci_dss },
                  { value: 'iso_27001', label: FRAMEWORK_LABELS.iso_27001 },
                ]} />
        <TextInput label="Versão do framework" placeholder="4.0.1"
                   value={versao} onChange={(e) => setVersao(e.currentTarget.value)} />
        <TextInput label="Emissor" required placeholder="Quem assinou o documento"
                   value={emissor} onChange={(e) => setEmissor(e.currentTarget.value)}
                   data-testid="certificado-emissor" />
        <TextInput label="Número do documento" placeholder="Clicksign 4a1d93b6-…"
                   value={documento} onChange={(e) => setDocumento(e.currentTarget.value)} />
        <Textarea label="Escopo, transcrito do documento" required autosize minRows={4}
                  description="Transcrição, não resumo. O que não estiver no documento
                               precisa vir marcado como tal — é o que impede o selo de
                               afirmar mais do que o papel diz."
                  value={escopo} onChange={(e) => setEscopo(e.currentTarget.value)}
                  data-testid="certificado-escopo" />
        <Group grow>
          <TextInput label="Emitido em" required placeholder="AAAA-MM-DD"
                     value={emitido} onChange={(e) => setEmitido(e.currentTarget.value)} />
          <TextInput label="Vence em" required placeholder="AAAA-MM-DD"
                     description="Obrigatório. Quando o documento não traz vencimento, a data
                                  é sua decisão — e fica registrada como tal."
                     value={vence} onChange={(e) => setVence(e.currentTarget.value)}
                     data-testid="certificado-vence" />
        </Group>
        {/* O backend JÁ aceitava este campo desde a MIGRATION_346; o formulário é que nunca o
            oferecia, então todo certificado nascia com `documento_kb_id` nulo e sem caminho para
            deixar de ser (spec §12.4). */}
        <SelecionarDocumentoKb valor={documentoKb} onChange={setDocumentoKb}
                               testId="certificado-documento-kb" />
        <Group justify="flex-end">
          <Button variant="default" onClick={onFechar}>Cancelar</Button>
          <Button onClick={enviar} loading={registrar.isPending}
                  disabled={!emissor.trim() || !escopo.trim() || !emitido || !vence}
                  data-testid="certificado-salvar">
            Registrar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function ModalDetalhe({ id, onFechar }: { id: number; onFechar: () => void }) {
  const { data: cert } = useCertificado(id)
  const revogar = useRevogarCertificado()
  const vincular = useVincularAmbiente(id)
  const desvincular = useDesvincularAmbiente(id)
  const vincularDoc = useVincularDocumento(id)
  const tenants = useTenantsSelecionaveis()

  const [tenantId, setTenantId] = useState<string | null>(null)
  const [tipo, setTipo] = useState<TipoVinculo>('interno')
  const [revisarEm, setRevisarEm] = useState('')
  const [dataVirada, setDataVirada] = useState('')
  const [motivo, setMotivo] = useState('')

  const noEscopo = (cert?.ambientes ?? []).filter((a) => a.tipo === 'escopo')
  const foraDoEscopo = (cert?.ambientes ?? []).filter((a) => a.tipo !== 'escopo')

  // `data_virada` só viaja em `transicao`, e vazio vira `null` — string vazia reprovaria no
  // `pattern` do backend, e mandar a data num `escopo` volta 422. O formulário não oferece o
  // campo nos outros tipos, mas o estado sobrevive à troca do Select: limpar aqui é o que
  // impede que uma data digitada antes da troca vá junto.
  const enviarVinculo = () => vincular.mutate({
    tenant_id: Number(tenantId), tipo, revisar_em: revisarEm.trim(), motivo: motivo.trim(),
    data_virada: tipo === 'transicao' && dataVirada.trim() ? dataVirada.trim() : null,
  }, {
    onSuccess: (r) => {
      notifications.show({
        color: 'green',
        message: `Ambiente vinculado. has_pci do tenant: ${r.has_pci ? 'sim' : 'não'}.`,
      })
      setTenantId(null); setRevisarEm(''); setDataVirada(''); setMotivo('')
    },
    onError: (e) => notifications.show({
      color: 'red', message: mensagemDeErro(e, 'Não foi possível vincular.'),
    }),
  })

  return (
    <Modal opened onClose={onFechar} size="xl" title={
      cert ? `${FRAMEWORK_LABELS[cert.framework]} — ${cert.emissor}` : 'Certificado'
    }>
      {!cert ? <Text c="dimmed">Carregando…</Text> : (
        <Stack gap="md">
          <Group gap="xs">
            <Badge variant="light" color={ESTADO_CORES[cert.estado] ?? 'gray'}>
              {cert.estado}
            </Badge>
            <Text size="sm" c="dimmed">
              {dataBr(cert.emitido_em)} → {dataBr(cert.vence_em)}
              {cert.documento_numero ? ` · ${cert.documento_numero}` : ''}
            </Text>
          </Group>

          {/* O escopo INTEIRO, sem truncar: é o que separa o selo do booleano. */}
          <div>
            <Text fw={600} size="sm" mb={4}>Escopo, como está no documento</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}
                  data-testid="certificado-escopo-texto">
              {cert.escopo_texto}
            </Text>
          </div>

          {/* O vínculo com o documento arquivado: EDITÁVEL e VISÍVEL.
              Até 29/09/2026 o campo existia na tabela, era aceito pelo POST e declarado nos
              tipos — e nenhuma tela o preenchia ou o mostrava. Ficava com aparência de pronto
              (spec §12.4). */}
          <div>
            <Text fw={600} size="sm" mb={4}>Documento arquivado</Text>
            <Text size="sm" c={cert.documento_kb_titulo ? undefined : 'dimmed'}
                  data-testid="certificado-documento-titulo">
              {textoDoDocumento(cert)}
            </Text>
            {cert.documento_kb_id != null && (
              // A página da KB não aceita deep-link para um documento, então o link vai para a
              // base e o rótulo diz exatamente isso. Prometer "abrir o documento" e cair numa
              // lista seria uma mentira pequena e gratuita.
              <Anchor href="/conhecimento" size="xs" data-testid="certificado-documento-link">
                abrir a base de conhecimento
              </Anchor>
            )}
            <Group grow align="flex-end" mt="xs">
              <SelecionarDocumentoKb
                valor={cert.documento_kb_id != null ? String(cert.documento_kb_id) : null}
                onChange={(v) => vincularDoc.mutate(v ? Number(v) : null, {
                  onSuccess: () => notifications.show({
                    color: 'green',
                    message: v ? 'Documento vinculado.' : 'Documento desvinculado.',
                  }),
                  onError: (e) => notifications.show({
                    color: 'red',
                    message: mensagemDeErro(e, 'Não foi possível alterar o documento.'),
                  }),
                })}
                testId="certificado-documento-select" />
            </Group>
          </div>

          <div>
            <Text fw={600} size="sm" mb={4}>No escopo ({noEscopo.length})</Text>
            {noEscopo.length === 0
              ? <Text size="sm" c="dimmed">nenhum ambiente no escopo</Text>
              : <ListaDeAmbientes itens={noEscopo}
                                  onRemover={(t) => desvincular.mutate(t)} />}
          </div>

          <div>
            <Text fw={600} size="sm" mb={4}>Fora do escopo ({foraDoEscopo.length})</Text>
            {foraDoEscopo.length === 0
              ? <Text size="sm" c="dimmed">nenhum ambiente fora do escopo</Text>
              : <ListaDeAmbientes itens={foraDoEscopo}
                                  onRemover={(t) => desvincular.mutate(t)} />}
          </div>

          <WidgetCard>
            <Stack gap="sm">
              <Text fw={600} size="sm">Vincular ambiente</Text>
              <Group grow align="flex-start">
                <Select label="Ambiente" searchable data={tenants} value={tenantId}
                        onChange={setTenantId} data-testid="vinculo-tenant" />
                <Select label="Tipo" value={tipo} allowDeselect={false}
                        onChange={(v) => setTipo((v as TipoVinculo) ?? 'interno')}
                        data={(['escopo', 'interno', 'transicao'] as TipoVinculo[]).map((t) => ({
                          value: t, label: TIPO_LABELS[t],
                        }))} data-testid="vinculo-tipo" />
              </Group>
              <Group grow align="flex-start">
                <TextInput label="Revisar em" required placeholder="AAAA-MM-DD"
                           description="Obrigatório nos três tipos: selo sem prazo de revisão
                                        vira promessa sem data."
                           value={revisarEm}
                           onChange={(e) => setRevisarEm(e.currentTarget.value)}
                           data-testid="vinculo-revisar" />
                <TextInput label="Motivo" required
                           description="Por que este ambiente está aqui."
                           value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)}
                           data-testid="vinculo-motivo" />
              </Group>
              {/* Só em `transicao`: `escopo` e `interno` não têm virada, e o backend recusa a
                  data neles com 422. Em branco é a resposta CERTA enquanto ninguém decidiu —
                  é o estado dos 5 vínculos de produção hoje. */}
              {tipo === 'transicao' && (
                <TextInput label="Data da virada (quando existir)" placeholder="AAAA-MM-DD"
                           description="Em branco é a resposta certa enquanto a data não foi
                                        decidida: o selo diz 'sem data de virada'. Preenchida,
                                        ele passa a afirmar a virada para esta data."
                           value={dataVirada}
                           onChange={(e) => setDataVirada(e.currentTarget.value)}
                           data-testid="vinculo-data-virada" />
              )}
              <Group justify="flex-end">
                <Button leftSection={<IconCirclePlus size={14} />} onClick={enviarVinculo}
                        loading={vincular.isPending}
                        disabled={!tenantId || !revisarEm || !motivo.trim()}
                        data-testid="vinculo-salvar">
                  Vincular
                </Button>
              </Group>
            </Stack>
          </WidgetCard>

          {cert.revogado_em == null && (
            <Group justify="flex-end">
              <Button color="red" variant="light" leftSection={<IconTrash size={14} />}
                      loading={revogar.isPending}
                      onClick={() => revogar.mutate(cert.id, {
                        onSuccess: (r) => notifications.show({
                          color: 'green',
                          message: `Certificado revogado. ${r.tenants_reavaliados.length} `
                            + 'ambiente(s) reavaliado(s).',
                        }),
                        onError: (e) => notifications.show({
                          color: 'red', message: mensagemDeErro(e, 'Não foi possível revogar.'),
                        }),
                      })}
                      data-testid="certificado-revogar">
                Revogar certificado
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </Modal>
  )
}

/** O que a tela diz sobre o documento arquivado — **três** estados, não dois.
 *
 *  O terceiro só existe porque o título é decisão de ACESSO: a rota o preenche com
 *  `fontes_para_leitor`, que devolve `null` para quem não lê o documento. Então um certificado
 *  PODE ter documento e o título vir vazio, e aí `titulo ?? 'nenhum documento vinculado'`
 *  **mentiria** — diria que não há documento quando há. É a mesma classe de erro que esta entrega
 *  inteira existe para fechar, uma casa adiante.
 *
 *  Exportada porque é o que o teste decide.
 */
export function textoDoDocumento(
  cert: { documento_kb_id: number | null; documento_kb_titulo: string | null },
): string {
  if (cert.documento_kb_id == null) return 'nenhum documento da base de conhecimento vinculado'
  if (cert.documento_kb_titulo == null) {
    return 'há um documento vinculado, e você não tem acesso ao título dele'
  }
  return cert.documento_kb_titulo
}

/** Seletor dos documentos que JÁ estão na base de conhecimento.
 *
 *  Não sobe arquivo: documento entra na KB pela KB (motor único). Aqui só se aponta para um que
 *  já existe — e é por isso que a lista vem de `listKbDocuments`, não de um upload próprio.
 *
 *  `indexed` no filtro: documento que ainda está indexando ou que falhou não é prova de nada, e
 *  oferecê-lo faria o vínculo apontar para um papel que a plataforma não consegue abrir.
 *
 *  **A lista vazia é um estado esperado, e tem texto próprio.** O `GET /assistant/kb/documents`
 *  filtra pelo PERFIL DE LEITURA DE PASTA de quem pergunta (`perfil_de_leitura`), então quem não
 *  pode ler o documento não o vê aqui — e isso está certo: não se vincula como prova um papel que
 *  a pessoa não pode abrir. Mas vazio sem explicação parece feature quebrada, e é por isso que os
 *  três estados (carregando / erro / vazio) dizem coisas diferentes.
 */
export function SelecionarDocumentoKb({ valor, onChange, testId }: {
  valor: string | null
  onChange: (v: string | null) => void
  testId: string
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['kb', 'documents', 'indexed'],
    queryFn: () => listKbDocuments({ status: 'indexed' }),
    staleTime: 60_000,
  })
  const itens = (data?.items ?? []).map((d) => ({ value: String(d.id), label: d.titulo }))
  const vazio = !isLoading && !isError && itens.length === 0
  return (
    <Select label="Documento na base de conhecimento" searchable clearable
            placeholder={placeholderDoSeletor({ isLoading, isError, vazio })}
            description="Opcional. Aponta para um documento que já está na KB — esta tela não
                         sobe arquivo. Em branco significa que o exemplar não está arquivado
                         aqui, e o número do documento continua identificando-o."
            data={itens} value={valor} onChange={onChange}
            disabled={isLoading || isError || vazio}
            data-testid={testId} />
  )
}

/** Os três estados que não são "escolha um", cada um com o seu texto.
 *
 *  Exportada porque é o que o teste decide: um placeholder só de `'nenhum'` faria lista vazia por
 *  falta de PERMISSÃO DE PASTA parecer campo não preenchido, e alguém concluiria que a tela está
 *  quebrada quando ela está obedecendo ao perfil de leitura.
 */
export function placeholderDoSeletor(
  { isLoading, isError, vazio }: { isLoading: boolean; isError: boolean; vazio: boolean },
): string {
  if (isLoading) return 'carregando os documentos…'
  if (isError) return 'não foi possível listar os documentos'
  if (vazio) return 'nenhum documento visível para você na base de conhecimento'
  return 'escolha um documento'
}

function ListaDeAmbientes({ itens, onRemover }: {
  itens: { tenant_id: number; name: string; environment: string | null; tipo: TipoVinculo;
           revisar_em: string; data_virada: string | null; motivo: string }[]
  onRemover: (tenantId: number) => void
}) {
  return (
    <Table>
      <Table.Tbody>
        {itens.map((a) => (
          <Table.Tr key={a.tenant_id} data-testid={`ambiente-${a.tenant_id}`}>
            <Table.Td>
              <Text size="sm" fw={600}>{a.name}</Text>
              <Text size="xs" c="dimmed">{(a.environment ?? '?').toUpperCase()}</Text>
            </Table.Td>
            <Table.Td>
              <Badge size="sm" variant="light" color={TIPO_CORES[a.tipo]}>
                {TIPO_LABELS[a.tipo]}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm">revisar em {dataBr(a.revisar_em)}</Text>
              {/* Quem grava a data tem de VER o que gravou — e o `transicao` sem data mostra
                  isso explicitamente, em vez de deixar a linha calada. */}
              {a.tipo === 'transicao' && (
                <Text size="xs" c={a.data_virada ? undefined : 'dimmed'}
                      data-testid={`ambiente-virada-${a.tenant_id}`}>
                  {a.data_virada
                    ? `virada em ${dataBr(a.data_virada)}`
                    : 'sem data de virada'}
                </Text>
              )}
            </Table.Td>
            <Table.Td><Text size="sm" c="dimmed">{a.motivo}</Text></Table.Td>
            <Table.Td>
              <Button size="compact-sm" variant="subtle" color="red"
                      onClick={() => onRemover(a.tenant_id)}
                      data-testid={`ambiente-remover-${a.tenant_id}`}>
                Desvincular
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}
